import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { WORKSPACE_COLORS, WORKSPACE_TYPE_LABELS, WORKSPACE_TYPES, type WorkspaceColor, type WorkspaceType } from '@unitly/contracts';
import { Sheet } from '../../ui/Sheet.tsx';
import { SelectField, TextField, WorkspaceAvatar, wsColor } from '../../ui/primitives.tsx';
import { useCreateWorkspace } from '../../api/queries.ts';
import { useOrgCtx } from '../../app/org-context.tsx';
import { ApiError } from '../../api/client.ts';
import { useToast } from '../../ui/Toast.tsx';

const suggestKey = (name: string) =>
  name.normalize('NFD').replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 3);

export function CreateWorkspaceSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { orgId, base } = useOrgCtx();
  const create = useCreateWorkspace(orgId);
  const navigate = useNavigate();
  const toast = useToast();
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [keyTouched, setKeyTouched] = useState(false);
  const [type, setType] = useState<WorkspaceType>('media');
  const [color, setColor] = useState<WorkspaceColor>('pink');

  useEffect(() => {
    if (open) { setName(''); setKey(''); setKeyTouched(false); setType('media'); setColor('pink'); create.reset(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const err = create.error instanceof ApiError ? create.error : null;
  const effectiveKey = keyTouched ? key : suggestKey(name);

  return (
    <Sheet open={open} onClose={onClose} title="Nuovo spazio" variant="center" labelledBy="new-ws-title">
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate(
            { name, key: effectiveKey, type, color },
            { onSuccess: (ws) => { onClose(); toast(`Spazio ${ws.name} creato`); navigate(`${base}/w/${ws.key}`); } },
          );
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', padding: '6px 0 4px' }}>
          <WorkspaceAvatar name={name || 'Spazio'} keyLabel={effectiveKey || '—'} color={color} active size={84} />
        </div>
        <TextField data-autofocus label="Nome" placeholder="Es. Ragazze, Sales, Cliente Rossi" value={name} onChange={(e) => setName(e.target.value)} error={err?.errors.name} required maxLength={80} />
        <TextField
          label="Sigla"
          hint="Compare nei codici delle task, es. RAG-12"
          value={effectiveKey}
          onChange={(e) => { setKeyTouched(true); setKey(e.target.value.toUpperCase()); }}
          error={err?.errors.key ?? (err?.code === 'conflict' ? err.message : undefined)}
          required
          maxLength={10}
          autoCapitalize="characters"
        />
        <SelectField label="Tipo" value={type} onChange={(e) => setType(e.target.value as WorkspaceType)}>
          {WORKSPACE_TYPES.map((t) => <option key={t} value={t}>{WORKSPACE_TYPE_LABELS[t]}</option>)}
        </SelectField>
        <div className="field">
          <span>Colore</span>
          <div className="color-picker" role="group" aria-label="Colore">
            {WORKSPACE_COLORS.map((c) => (
              <button key={c} type="button" className="color-swatch" aria-label={c} aria-pressed={color === c} style={{ '--sw': wsColor(c) } as React.CSSProperties} onClick={() => setColor(c)} />
            ))}
          </div>
        </div>
        {err && !err.errors.name && !err.errors.key && err.code !== 'conflict' && <div className="form-error">{err.message}</div>}
        <button type="submit" className="btn btn-primary btn-block" disabled={!name.trim() || !effectiveKey || create.isPending}>
          Crea spazio
        </button>
      </form>
    </Sheet>
  );
}
